import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector } from '@angular/core';
import { DateTime } from 'luxon';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DatePickerOverlayComponent } from './date-picker-overlay/date-picker-overlay.component';
import { DatePickerOverlayRef } from './date-picker-overlay/date-picker-overlay-ref';
import { IBbitDatePickerOverlay } from './interfaces';

@Injectable({
  providedIn: 'root',
})
export class DatePickerService {
  constructor(private _overlay: Overlay) {}

  open({
    date,
    element,
    connectAt,
    unsubscribe,
  }: {
    date: string | DateTime;
    element: HTMLElement;
    connectAt?: 'end' | 'start';
    unsubscribe?: Subject<void>;
  }): IBbitDatePickerOverlay {
    if (!connectAt) {
      connectAt = 'end';
    }
    if (!unsubscribe) {
      unsubscribe = new Subject<void>();
    }
    if (!date) {
      date = DateTime.now();
    }

    const positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(element)
      .withPositions([
        {
          overlayX: 'start',
          overlayY: 'top',
          originX: connectAt,
          originY: 'bottom',
        },
        {
          overlayX: 'start',
          overlayY: 'bottom',
          originX: connectAt,
          originY: 'top',
        },
      ]);

    const config = new OverlayConfig({
      hasBackdrop: true,
      backdropClass: '',
      panelClass: '',
      width: '300px',
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy,
    });

    const dateChanged = new Subject<DateTime>();
    const closed = new Subject<void>();

    const overlayRef = this._overlay.create(config);
    const datePickerOverlayRef = new DatePickerOverlayRef(overlayRef);
    datePickerOverlayRef.setDate(date);
    datePickerOverlayRef.dateChangedSubject.pipe(takeUntil(unsubscribe)).subscribe((date: DateTime) => {
      dateChanged.next(date);
      datePickerOverlayRef.close();
      closed.next();
    });

    const injector = Injector.create({
      providers: [{ provide: DatePickerOverlayRef, useValue: datePickerOverlayRef }],
    });

    const containerPortal = new ComponentPortal(DatePickerOverlayComponent, null, injector);
    // const containerRef: ComponentRef<DatePickerOverlayComponent> =
    overlayRef.attach(containerPortal);

    overlayRef
      .backdropClick()
      .pipe(takeUntil(unsubscribe))
      .subscribe((): void => {
        datePickerOverlayRef.close();
        closed.next();
      });

    return {
      dateChanged,
      closed,
      close: () => {
        unsubscribe.next();
        unsubscribe.complete();
        dateChanged.complete();
        datePickerOverlayRef.close();
      },
    };
  }
}
